import PopoverMenu from '@components/common/PopoverMenu'

/**
 * A report filter as a PILL — spec 092, Figma 110:8927.
 *
 * The trigger reads "Label: Value"; the control itself moves into the popover.
 * Nothing about the filter's behaviour changes — same component, same onChange,
 * same wire value. Only where the control lives changes.
 *
 * THREE DETAILS THAT ARE LOAD-BEARING, not styling:
 *
 * 1. The label is its OWN text node and the colon is a separate aria-hidden one.
 *    Dozens of assertions across the suites do `getByText('Client')`, which is an
 *    exact normalised-text match — `<span>Client:</span>` fails it. Splitting the
 *    colon out also means the visible label still answers "does this report offer
 *    a Client filter?" while the popover is CLOSED, which is what keeps every
 *    negative assertion honest instead of passing vacuously against a closed
 *    popover.
 *
 * 2. `role="dialog"`, not "menu". The body holds real inputs, and PopoverMenu's
 *    menu mode preventDefaults Arrow/Home/End to rove between rows — the exact
 *    keys that move a caret in a date field or a search box.
 *
 * 3. The control inside must NOT render its own field label, or the label appears
 *    twice on the page and `getByText` starts throwing "found multiple".
 */
const FilterPill = ({
  label,
  /** Short "what is set" text. Null/empty shows `placeholder` instead. */
  summary,
  placeholder = 'Any',
  /** A criterion is set — the pill takes the chip palette. */
  active = false,
  width = 300,
  disabled = false,
  children,
}) => (
  <PopoverMenu
    role="dialog"
    label={`${label} filter options`}
    triggerLabel={`${label} filter`}
    align="left"
    width={width}
    disabled={disabled}
    triggerClassName={`btn-rc-pill max-w-[16rem]${active ? ' is-active' : ''}`}
    triggerContent={(
      <>
        <span className="flex-none">{label}</span>
        <span aria-hidden="true" className="flex-none opacity-60">:</span>
        <span className={`min-w-0 truncate font-normal ${active ? '' : 'text-rc-muted'}`}>
          {summary || placeholder}
        </span>
        <svg aria-hidden="true" className="ml-0.5 h-4 w-4 flex-none opacity-60" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8}>
          <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </>
    )}
  >
    <div className="px-3 py-2.5">{children}</div>
  </PopoverMenu>
)

export default FilterPill
